import React, { useMemo, useState } from "react";
import { Accordion, Badge, Button, Table } from "@themesberg/react-bootstrap";

import {
  PAGE_SIZE,
  formatCurrency,
  formatDate,
  getUsersAtLevel,
  getPurchaseBadge,
  getLevelBadge,
} from "./networkHelpers";

function LevelUserList({ users, visibleCount, onLoadMore }) {
  const visible = users.slice(0, visibleCount);
  const hidden = users.length - visible.length;

  if (users.length === 0) {
    return <div className="text-muted small py-2">No users at this level.</div>;
  }

  return (
    <>
      <div className="table-responsive d-none d-md-block">
        <Table className="dashboard-table align-middle mb-0">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Name</th>
              <th>Joined</th>
              <th>Referred By</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((user) => {
              const purchaseBadge = getPurchaseBadge(false);
              return (
                <tr key={user.id}>
                  <td className="fw-semibold">USR{user.customUserId}</td>
                  <td>{user.name}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>{user.referredByName}</td>
                  <td>
                    <Badge bg={getLevelBadge(user.level).bg} className="rounded-pill px-2 py-1">
                      {getLevelBadge(user.level).label}
                    </Badge>
                    <Badge bg={purchaseBadge.bg} className="rounded-pill px-2 py-1 ms-1">
                      {purchaseBadge.label}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-md-none">
        {visible.map((user) => (
          <div key={user.id} className="network-user-card">
            <div className="network-user-card-title">{user.name}</div>
            <div className="network-user-card-meta">
              {user.customUserId} · {formatDate(user.createdAt)}
            </div>
            <Badge bg={getLevelBadge(user.level).bg} className="mt-2">
              {getLevelBadge(user.level).label}
            </Badge>
          </div>
        ))}
      </div>

      {hidden > 0 && (
        <div className="text-center mt-2">
          <Button variant="outline-primary" size="sm" onClick={onLoadMore}>
            Load More ({hidden} hidden)
          </Button>
        </div>
      )}
    </>
  );
}

export default function LevelWiseNetworkTab({ levels = [], treeFlat = [], loading = false }) {
  const [activeKey, setActiveKey] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});

  const levelUsersMap = useMemo(() => {
    const map = {};
    levels.forEach((lvl) => {
      map[lvl.level] = getUsersAtLevel(treeFlat, lvl.level);
    });
    return map;
  }, [levels, treeFlat]);

  const loadMore = (level) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [level]: (prev[level] || PAGE_SIZE) + PAGE_SIZE,
    }));
  };

  if (loading) {
    return <div className="text-center py-5 text-muted">Loading level data...</div>;
  }

  if (levels.length === 0) {
    return <div className="text-center py-5 text-muted">No level data found.</div>;
  }

  return (
    <Accordion
      activeKey={activeKey}
      onSelect={(k) => {
        setActiveKey(k);
        if (k) {
          const levelNum = Number(k);
          if (!visibleCounts[levelNum]) {
            setVisibleCounts((prev) => ({ ...prev, [levelNum]: PAGE_SIZE }));
          }
        }
      }}
      className="network-level-accordion"
    >
      {levels.map((lvl) => {
        const users = levelUsersMap[lvl.level] || [];
        const key = String(lvl.level);
        const visibleCount = visibleCounts[lvl.level] || PAGE_SIZE;

        return (
          <Accordion.Item eventKey={key} key={lvl.level}>
            <Accordion.Header>
              <div className="d-flex flex-wrap justify-content-between align-items-center w-100 pe-3 gap-2">
                <span>
                  Level {lvl.level} — {lvl.totalUsers} user{lvl.totalUsers !== 1 ? "s" : ""}
                </span>
                <span className="text-muted small fw-normal">
                  Business {formatCurrency(lvl.businessGenerated)} · Commission{" "}
                  {formatCurrency(lvl.commissionEarned)}
                </span>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <LevelUserList
                users={users}
                visibleCount={visibleCount}
                onLoadMore={() => loadMore(lvl.level)}
              />
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}
